/**
 * Whose exercise this is, and therefore what may be done to it.
 *
 * One decision read by four surfaces — the table's author column, the panel's „Twoje" badge, the
 * theatre's ownership action and its `…` menu — because the backend answers the same question four
 * different ways and a component working it out from two booleans gets it wrong the first time a
 * trainer customises a library movement.
 *
 * ── Three states, and the middle one is the one that is always missed ───────────────────────
 * · **`own`** — the trainer created it. `PATCH /exercise/{id}` edits it, `DELETE` removes it, and
 *   its links travel on that same PATCH.
 * · **`libraryCustomised`** — a LIBRARY movement this trainer has overlaid. It is not theirs; what
 *   is theirs is the overlay, and the way back is `DELETE /exercise/{id}/overlay`. Drawn as a
 *   library row, because the author is still MoveBuddy.
 * · **`library`** — the shared catalogue, untouched.
 *
 * ── Ownership is the OWNER ID, never `isCustom` alone ───────────────────────────────────────
 * `isCustom` is `personalTrainerId != null`, which says "somebody's", not "yours". `GET /exercise`
 * returns the shared catalogue plus the reader's own movements, so today the two coincide — but a
 * response that ever carried another trainer's exercise would light every edit control on it, and
 * the trainer would meet the backend's 404 instead of a control that was never offered. The id is
 * compared, and a `trainerId` this build does not have yet (the profile read is still open) claims
 * nothing.
 *
 * ── `isCustomized` is the BACKEND's word, and it is not `isCustom` ──────────────────────────
 * One letter apart on the wire and two different facts: `isCustom` is derived here from
 * `personalTrainerId`, `isCustomized` is the server saying this trainer has an overlay on this row.
 * They are never both true — an overlay on your own exercise is refused by design (403), which is
 * why own links go through PATCH.
 */

export type ExerciseOwnership = 'library' | 'libraryCustomised' | 'own';

/** What the exercise carries of its own identity. A structural subset, so a row can be asked too. */
export interface OwnableExercise {
  readonly ownerTrainerId: number | null;
  readonly isCustomized: boolean;
}

/**
 * Which of the three this movement is, for this trainer.
 *
 * `trainerId` is `null` while the profile read is still open, and the answer is then `library` for
 * everything — the read-only reading, which is the safe one: a control that appears a beat late is
 * a smaller defect than one that appears and then refuses.
 */
export function ownershipOf(
  exercise: OwnableExercise,
  trainerId: number | null,
): ExerciseOwnership {
  if (trainerId !== null && exercise.ownerTrainerId === trainerId) return 'own';
  // Not theirs. An overlay is the only thing they can have on it, and `isCustomized` is the server
  // saying they do.
  return exercise.isCustomized ? 'libraryCustomised' : 'library';
}

/** Which controls a movement offers. Every one of them is a real backend route. */
export interface ExerciseActions {
  /** `PATCH /exercise/{id}`. Refused with 403 on anything the trainer does not own. */
  readonly editable: boolean;
  /** Attach or change materials. ALWAYS offered — the ROUTE is what ownership decides. */
  readonly materials: boolean;
  /** `DELETE /exercise/{id}/overlay` — drop the override and read the library's links again. */
  readonly revertMaterials: boolean;
  /** `POST /exercise` seeded from this one. Offered on anything, because a copy is always yours. */
  readonly duplicable: boolean;
  /** `PUT /exercise/{id}/cover`. The picture belongs to the row, so only its owner may change it. */
  readonly coverChangeable: boolean;
  /** `DELETE /exercise/{id}`. 409 while a logged workout or a clip still references it. */
  readonly deletable: boolean;
}

/**
 * What a movement in this state offers.
 *
 * `materials` is true in all three, and that is not an oversight: a trainer may attach their own
 * video to a library movement — that is what the overlay IS — and the canon's ownership action on a
 * library row is „Dostosuj materiały" for exactly that reason. What differs is the endpoint, and
 * that belongs to whichever module writes them, not to the one that says what is offered.
 */
export function actionsFor(ownership: ExerciseOwnership): ExerciseActions {
  const own = ownership === 'own';
  return {
    editable: own,
    materials: true,
    revertMaterials: ownership === 'libraryCustomised',
    duplicable: true,
    coverChangeable: own,
    deletable: own,
  };
}
